import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, FileText, CreditCard, Users, BarChart3, Check, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

const BENEFIT_KEYS = [
  { icon: FileText, key: 'monthly' },
  { icon: CreditCard, key: 'deferred' },
  { icon: Users, key: 'centralized' },
  { icon: BarChart3, key: 'reporting' },
];

const PROCESS_STEPS = [
  { num: '01', key: 'step1' },
  { num: '02', key: 'step2' },
  { num: '03', key: 'step3' },
  { num: '04', key: 'step4' },
];

const FEATURE_KEYS = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6'];

const EntreprisesPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#f8f8f8]" data-testid="entreprises-page">
          <Seo {...seo.entreprises} />
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-white border-b border-neutral-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-block px-4 py-2 bg-black text-white rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-6">
                {t('entreprisesPage.badge')}
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-black tracking-tight mb-6">
                {t('entreprisesPage.title1')}<br />
                <span className="text-neutral-400">{t('entreprisesPage.title2')}</span>
              </h1>
              <p className="text-lg md:text-xl text-neutral-600 mb-8 max-w-lg leading-relaxed">
                {t('entreprisesPage.description')}
              </p>
              <Link to="/contact">
                <Button
                  className="bg-black text-white hover:bg-neutral-800 px-8 py-6 text-base font-semibold rounded-xl shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-0.5"
                  data-testid="enterprise-contact-btn"
                >
                  {t('entreprisesPage.ctaButton')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-neutral-50 border border-neutral-200 rounded-2xl p-8 md:p-10"
            >
              <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center mb-6">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-black mb-6">
                {t('entreprisesPage.includedTitle')}
              </h2>
              <ul className="space-y-4">
                {FEATURE_KEYS.map((featKey) => (
                  <li key={featKey} className="flex items-center gap-3 text-neutral-700">
                    <div className="w-6 h-6 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    {t(`entreprisesPage.features.${featKey}`)}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-[#f8f8f8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-black tracking-tight mb-4">
              {t('entreprisesPage.howItWorks.title')}
            </h2>
            <p className="text-neutral-500 max-w-2xl mx-auto text-lg">
              {t('entreprisesPage.howItWorks.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFIT_KEYS.map((benefit, i) => (
              <motion.div
                key={benefit.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-white border border-neutral-100 rounded-2xl p-8 hover:border-neutral-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-neutral-100 group-hover:bg-black rounded-xl flex items-center justify-center mb-5 transition-colors duration-300">
                  <benefit.icon className="w-6 h-6 text-neutral-700 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-black mb-2">
                  {t(`entreprisesPage.benefits.${benefit.key}.title`)}
                </h3>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  {t(`entreprisesPage.benefits.${benefit.key}.desc`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-black tracking-tight mb-12 text-center">
              {t('entreprisesPage.process.title')}
            </h2>

            <div className="space-y-6">
              {PROCESS_STEPS.map((step, i) => (
                <motion.div
                  key={step.key}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex gap-6 items-start p-6 bg-neutral-50 border border-neutral-100 rounded-2xl hover:border-neutral-200 hover:shadow-md transition-all duration-300"
                >
                  <div className="text-4xl font-heading font-bold text-neutral-300">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-black mb-1">
                      {t(`entreprisesPage.process.${step.key}.title`)}
                    </h3>
                    <p className="text-neutral-600">{t(`entreprisesPage.process.${step.key}.desc`)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              {t('entreprisesPage.cta.title1')}<br />
              <span className="text-neutral-400">{t('entreprisesPage.cta.title2')}</span>
            </h2>
            <p className="text-neutral-400 max-w-xl mx-auto mb-10 text-lg">
              {t('entreprisesPage.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button
                  className="bg-white text-black hover:bg-neutral-100 px-10 py-6 text-base font-semibold rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                  data-testid="enterprise-cta-contact"
                >
                  {t('entreprisesPage.cta.contactBtn')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0939242320" className="flex items-center gap-2 text-neutral-400 hover:text-white transition-colors">
                <Phone className="w-5 h-5" />
                <span className="font-medium">09 39 24 23 20</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EntreprisesPage;
